import { ArrowDown, X } from 'react-feather';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RegistrationSummary } from '../helpers';
import { Section, SectionTitle } from '../../Common/Layout';
import { useRegistrationsUrlState } from './helpers';
import RegistrationDetails from './RegistrationDetails';
import UI from '../../../common/UI';
import useMemoizedValue from '../../../common/useMemoizedValue';

export interface PreregistrationsSectionProps {
  summary: RegistrationSummary;
  perPage?: number;
}

const PreregistrationsSection = ({ summary, perPage = 10 }: PreregistrationsSectionProps) => {
  const { t } = useTranslation();

  const [urlState, setUrlState] = useRegistrationsUrlState();

  const showMore = () => {
    setUrlState((params) => ({
      ...params,
      preregistrations: {
        ...params.preregistrations,
        limit: params.preregistrations.limit + perPage,
      },
    }), true);
  };

  const showLess = () => {
    setUrlState((params) => ({
      ...params,
      preregistrations: {
        ...params.preregistrations,
        limit: null,
      },
    }), true);
  };

  const [loading, setLoading] = useState(false);
  const params = useMemoizedValue(urlState.preregistrations);

  useEffect(() => {
    setLoading(true);
  }, [params]);

  useEffect(() => {
    setLoading(false);
  }, [summary]);

  const registrations = summary.preregistrations.slice(0, urlState.preregistrations.limit);

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }} id="TicketsSection">
      <UI.Div>
        <UI.GridContainer sc={{ columns: '1fr fit-content(150px)' }}>
          <UI.Div>
            <SectionTitle
              count={summary.preregistrations_count > 1 ? summary.preregistrations_count : undefined}
              loading={loading}
            >
              {t('your_preregistrations', { count: summary.preregistrations_count })}
            </SectionTitle>
          </UI.Div>
        </UI.GridContainer>
      </UI.Div>

      <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
        {registrations.map((registration) => (
          <RegistrationDetails
            registration={registration}
            summary={summary}
            key={registration.id}
          />
        ))}

        {registrations.length === 0 && (
          <Section secondary>
            <UI.FlexContainer sc={{ alignItems: 'center' }} style={{ minHeight: 80 }}>
              <UI.Div sc={{ textAlign: 'center', width: 100, color: 'gray.600' }}>
                {t('no_results')}
              </UI.Div>
            </UI.FlexContainer>
          </Section>
        )}

        {summary.preregistrations_count > perPage && (
          <UI.GridContainer
            sc={{
              columns: urlState.preregistrations.limit > perPage ? 'fit-content(150px) 2fr' : '1fr',
              gutter: 0.5,
              px: [2, 4],
            }}
          >
            {urlState.preregistrations.limit > perPage && (
              <UI.Button
                onClick={() => showLess()}
                sc={{ outline: true, borderRadius: 'xl', size: 'lg', fontSize: 1 }}
              >
                <UI.Icon>
                  <X />
                </UI.Icon>
                {' '}
                {t('hide')}
              </UI.Button>
            )}
            <UI.Button
              onClick={() => showMore()}
              disabled={urlState.preregistrations.limit >= summary.preregistrations_count || loading}
              sc={{ outline: true, borderRadius: 'xl', size: 'lg', fontSize: 1 }}
            >
              <UI.Icon>
                <ArrowDown />
              </UI.Icon>
              {' '}
              {t('show_more')}
            </UI.Button>
          </UI.GridContainer>
        )}
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export default PreregistrationsSection;
